import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import {
  FileBarChart, Clock, Timer, Megaphone, ClipboardList, Sparkles, Tag,
  CalendarRange, Coins, Info, Building2,
} from 'lucide-react';
import { getReports } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { compactNumber, formatDuration } from '../utils/format';
import StatTile from '../components/ui/StatTile';
import SectionHeader from '../components/ui/SectionHeader';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';

const TEAL = '#0d9488';
const INDIGO = '#6366f1';
const AMBER = '#f59e0b';

const CURRENCY = { GBP: '£', USD: '$', EUR: '€', TRY: '₺' };

/** "£240–£720", or a dash when there is nothing to estimate from. */
function spendRange(min, max, currency) {
  if (min == null || max == null) return '—';
  const s = CURRENCY[currency] || '';
  return min === max ? `${s}${min.toLocaleString()}` : `${s}${min.toLocaleString()}–${s}${max.toLocaleString()}`;
}

/** A ranked list rendered as labelled share bars — used for most sections. */
function RankBars({ items, emptyText = 'No data yet', valueLabel }) {
  if (!items?.length) return <p className="text-sm text-ink-300">{emptyText}</p>;
  const max = Math.max(...items.map((i) => i.count));
  return (
    <div className="space-y-1.5">
      {items.map((i) => (
        <div key={i.value} className="flex items-center gap-2 text-sm">
          <span className="w-44 shrink-0 truncate text-ink-700" title={i.value}>{i.value}</span>
          <span className="flex-1 h-2 rounded-full bg-ink-100 overflow-hidden">
            <span className="block h-full bg-dental-500" style={{ width: `${(i.count / max) * 100}%` }} />
          </span>
          <span className="w-20 text-right text-ink-500 tabular-nums">
            {i.count}{valueLabel ? ` ${valueLabel}` : ''} · {i.share}%
          </span>
        </div>
      ))}
    </div>
  );
}

/** Denominator caption — every section says what it is a share OF. */
function Denominator({ children }) {
  return <p className="text-xs text-ink-400 mt-2">{children}</p>;
}

function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // CPM overrides applied without saving, so assumptions can be tried live.
  const [minCpm, setMinCpm] = useState('');
  const [maxCpm, setMaxCpm] = useState('');
  const [frequency, setFrequency] = useState('');
  const [applied, setApplied] = useState(0);

  useEffect(() => {
    document.title = 'Reports | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const params = {};
    if (minCpm) params.minCpm = minCpm;
    if (maxCpm) params.maxCpm = maxCpm;
    if (frequency) params.frequency = frequency;

    getReports(params)
      .then((res) => { if (active) setData(res.data.data); })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load reports')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50 ring-red-200">
        <p className="text-red-700">{error}</p>
        <button onClick={() => setApplied((n) => n + 1)} className="btn-secondary mt-2">Try again</button>
      </div>
    );
  }

  if (!data || data.coverage.events === 0) {
    return <EmptyState icon={FileBarChart} title="Nothing to report yet" message="Fetch and analyse some ads first." />;
  }

  const { coverage, leadTime, duration, creative, leadForms, treatments, offers, seasonality, spend } = data;
  const cur = spend.cpm.currency;

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <div>
        <h1 className="page-title mb-1">Reports</h1>
        <p className="text-sm text-ink-500">
          Everything the ad data supports, in one place — when competitors start promoting, how
          long they keep ads live, what they build, what they ask prospects for, and what it
          plausibly costs them.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <StatTile icon={CalendarRange} label="Events analysed" value={coverage.events} tone="dental" />
        <StatTile icon={Megaphone} label="Distinct ads" value={coverage.ads} tone="blue" />
        <StatTile icon={Building2} label="Clinics" value={coverage.clinics} tone="accent" />
        <StatTile
          icon={Clock}
          label="Median lead time"
          value={leadTime.medianDays != null ? `${leadTime.medianDays} days` : '—'}
          subValue={`${leadTime.eventsMeasured} events`}
          tone="green"
        />
        <StatTile
          icon={Timer}
          label="Median ad run"
          value={duration.medianDays != null ? formatDuration(duration.medianDays) : '—'}
          subValue={`${duration.adsMeasured} finished`}
          tone="ink"
        />
      </div>

      {/* Lead time */}
      <div className="card">
        <SectionHeader
          icon={Clock}
          title="How far ahead campaigns start"
          subtitle="Days between the first ad going live and the event date, per event"
        />
        {leadTime.eventsMeasured === 0 ? (
          <p className="text-sm text-ink-300">No event has both an ad start date and an event date yet.</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={leadTime.distribution}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={TEAL} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="text-sm">
                <p className="text-ink-600 mb-2">
                  Median <strong>{leadTime.medianDays} days</strong>, range {leadTime.minDays}–{leadTime.maxDays}.
                </p>
                <p className="text-xs font-semibold text-ink-700 mb-1">Longest lead times</p>
                <ul className="text-xs text-ink-600 space-y-0.5">
                  {leadTime.earliest.slice(0, 6).map((e, i) => (
                    <li key={`${e.eventTitle}-${i}`} className="truncate">
                      {e.leadDays}d · {e.clinicName || '—'} — {e.eventTitle || 'Untitled'}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Denominator>
              {leadTime.eventsMeasured} of {coverage.events} events. The crawler only scans ads from
              2025-01-01, so campaigns that began in 2024 for a 2025 event cannot appear — the long
              end is under-counted.
            </Denominator>
          </>
        )}
      </div>

      {/* Ad longevity */}
      <div className="card">
        <SectionHeader icon={Timer} title="How long ads stay live" subtitle="Finished ads only" />
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={duration.distribution}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill={INDIGO} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <Denominator>
          {duration.adsMeasured} finished ads. {duration.ongoingAds} still running are excluded —
          their span is still growing. {duration.unknownAds} have no usable dates.
        </Denominator>
      </div>

      {/* Creative & funnel */}
      <div className="card">
        <SectionHeader icon={Sparkles} title="What competitors build" subtitle="Creative format, call to action and placement" />
        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <p className="text-xs font-semibold text-ink-700 mb-2">Format</p>
            <RankBars items={creative.formats} />
          </div>
          <div>
            <p className="text-xs font-semibold text-ink-700 mb-2">Call to action</p>
            <RankBars items={creative.ctas} />
          </div>
          <div>
            <p className="text-xs font-semibold text-ink-700 mb-2">Platform</p>
            <RankBars items={creative.platforms} />
          </div>
        </div>
        <Denominator>Across {creative.adsMeasured} distinct ads.</Denominator>
      </div>

      {/* Lead forms */}
      <div className="card">
        <SectionHeader
          icon={ClipboardList}
          title="What the lead forms ask for"
          subtitle="Straight from the ads' own form fields — what competitors collect and push"
        />
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-semibold text-ink-700 mb-2">Most common questions</p>
            <RankBars items={leadForms.topQuestions} emptyText="No lead forms captured yet" />
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-ink-700 mb-2">Contact channel requested</p>
              <div className="flex flex-wrap gap-2 text-sm">
                {Object.entries(leadForms.channels).map(([k, v]) => (
                  <span key={k} className="badge bg-ink-100 text-ink-700">
                    {k}: <strong className="ml-1">{v}</strong>
                  </span>
                ))}
              </div>
              {leadForms.clinicsAskingWhatsapp.length > 0 && (
                <p className="text-xs text-ink-500 mt-2">
                  Asking for WhatsApp: {leadForms.clinicsAskingWhatsapp.join(', ')}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-ink-700 mb-2">Destinations</p>
              <RankBars items={leadForms.topDomains} emptyText="No outbound links captured" />
            </div>
          </div>
        </div>
        <Denominator>
          {leadForms.adsWithForm} of {leadForms.adsTotal} ads carry a lead form. Consent and
          thank-you boilerplate is filtered out of the question list.
        </Denominator>
      </div>

      {/* Treatments */}
      <div className="card">
        <SectionHeader icon={Tag} title="Treatment demand" subtitle="What is being advertised, overall and by region" />
        <div className="grid md:grid-cols-2 gap-5">
          <RankBars items={treatments.overall} valueLabel="" />
          <div className="space-y-2">
            {treatments.byRegion.slice(0, 6).map((r) => (
              <div key={r.region} className="text-sm">
                <span className="font-medium text-ink-800">{r.region}</span>
                <span className="text-ink-400 text-xs"> — {r.top.map((t) => t.value).join(', ') || '—'}</span>
              </div>
            ))}
          </div>
        </div>
        <Denominator>
          Across {treatments.eventsMeasured} events. The treatment vocabulary is open, so expect a
          tail beyond the 15 canonical types.
        </Denominator>
      </div>

      {/* Offers + seasonality */}
      <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
        <div className="card">
          <SectionHeader icon={Tag} title="Offer prevalence" subtitle="How often an event advertises a hook" />
          <p className="text-3xl font-bold text-ink-900">{offers.share}%</p>
          <p className="text-sm text-ink-500 mb-3">
            {offers.withOffer} of {offers.eventsMeasured} events advertise an offer
          </p>
          <div className="space-y-1 text-xs text-ink-600">
            {offers.byProvider.map((p) => (
              <div key={p.provider}>{p.provider}: {p.withOffer}/{p.total} ({p.share}%)</div>
            ))}
          </div>
          <Denominator>
            Presence only. The offer text is free-form with no vocabulary, so counting distinct
            offers would measure phrasing, not strategy.
          </Denominator>
        </div>

        <div className="card">
          <SectionHeader icon={CalendarRange} title="Seasonality" subtitle="Events by month, on the event date" />
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={seasonality.byMonth}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {seasonality.byMonth.map((m) => (
                  <Cell key={m.month} fill={m.month === seasonality.peakMonth ? AMBER : TEAL} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <Denominator>
            {seasonality.eventsMeasured} dated events{seasonality.peakMonth ? `, busiest in ${seasonality.peakMonth}` : ''}.
          </Denominator>
        </div>
      </div>

      {/* Estimated spend */}
      <div className="card">
        <SectionHeader
          icon={Coins}
          title="Estimated spend"
          subtitle="Derived from reach and a CPM range — the Ad Library reports no money at all"
        />

        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div className="w-28">
            <label htmlFor="minCpm" className="block text-xs font-medium text-ink-600 mb-1">Min CPM</label>
            <input id="minCpm" type="number" min="0" step="0.5" className="input w-full"
              placeholder={String(spend.cpm.minCpm)} value={minCpm} onChange={(e) => setMinCpm(e.target.value)} />
          </div>
          <div className="w-28">
            <label htmlFor="maxCpm" className="block text-xs font-medium text-ink-600 mb-1">Max CPM</label>
            <input id="maxCpm" type="number" min="0" step="0.5" className="input w-full"
              placeholder={String(spend.cpm.maxCpm)} value={maxCpm} onChange={(e) => setMaxCpm(e.target.value)} />
          </div>
          <div className="w-32">
            <label htmlFor="freq" className="block text-xs font-medium text-ink-600 mb-1">Frequency</label>
            <input id="freq" type="number" min="0.1" step="0.1" className="input w-full"
              placeholder={String(spend.cpm.frequency)} value={frequency} onChange={(e) => setFrequency(e.target.value)} />
          </div>
          <button type="button" className="btn-primary" onClick={() => setApplied((n) => n + 1)}>Recalculate</button>
        </div>

        <div className="rounded-xl bg-ink-50 ring-1 ring-ink-200 p-3 flex gap-2.5 mb-4">
          <Info className="w-4 h-4 text-ink-400 shrink-0 mt-0.5" />
          <p className="text-xs text-ink-600">
            An estimate, not a measurement: <code>spend = reach × frequency ÷ 1000 × CPM</code>, at{' '}
            {spend.cpm.minCpm}–{spend.cpm.maxCpm} {cur} CPM and frequency {spend.cpm.frequency}.
            At frequency 1 this is literally &ldquo;cost per 1000 people reached&rdquo;; raise it only
            if you want to assume how often each person saw the ad. Only the{' '}
            {spend.adsWithReach} ads with exact reach count — enrich more on Ad Statistics to widen it.
          </p>
        </div>

        {spend.adsWithReach === 0 ? (
          <p className="text-sm text-ink-300">
            No ad has exact reach yet. Press &ldquo;Update&rdquo; on an event in Ad Statistics to pull it.
          </p>
        ) : (
          <>
            <p className="text-sm text-ink-500 mb-1">Upper bound across all ads with reach</p>
            <p className="text-3xl font-bold text-ink-900 mb-1">
              {spendRange(spend.upperBound.min, spend.upperBound.max, cur)}
            </p>
            <p className="text-xs text-ink-400 mb-4">
              This one DOES add reach together, so overlapping audiences are counted more than once —
              treat it as a ceiling, not a total.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="border-b border-ink-200 text-ink-600 text-xs">
                    <th className="px-2 py-2 text-left font-semibold">Clinic</th>
                    <th className="px-2 py-2 text-right font-semibold">Ads</th>
                    <th className="px-2 py-2 text-right font-semibold">With reach</th>
                    <th className="px-2 py-2 text-right font-semibold">Estimated (largest ad)</th>
                  </tr>
                </thead>
                <tbody>
                  {spend.byClinic.map((c) => (
                    <tr key={c.clinicName} className="border-b border-ink-100 last:border-0">
                      <td className="px-2 py-2 text-ink-800">{c.clinicName}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-ink-600">{c.adCount}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-ink-600">{c.adsWithReach}</td>
                      <td
                        className="px-2 py-2 text-right tabular-nums font-semibold text-ink-900"
                        title={`Summed across its ads: ${spendRange(c.summedMin, c.summedMax, cur)} — audiences overlap`}
                      >
                        {spendRange(c.estimatedMin, c.estimatedMax, cur)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Reports;
